/* eslint-disable max-lines */
import {
  Component, Inject, AfterViewInit, OnDestroy, QueryList,
  ViewChildren, ElementRef, ViewChild, OnInit, HostListener, ChangeDetectorRef
} from '@angular/core';
import { Router, ActivationEnd, NavigationEnd, ActivatedRoute } from '@angular/router';
import { combineLatest, iif, Observable, of, Subscription, zip } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { environment as ENV } from '@environment';
import { Header } from '@header/entities/header-type';
import { HeaderFacade } from '@header/header.facade';
import { homeRootRoute } from '@app/modules/home/home-routing.module';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { setStatusBarColor } from '@ux/animations/device-status-bar.animations';
import { AppFacade } from '@app/app.facade';
import { UserInfo, UserLoyaltyType } from '@app/modules/core/interfaces/user';
import { ModuleEntity } from '@app/entities/modules-country';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { COUNTRY_MODULES_COLLECTION, MODULES_ROUTING } from '@app/modules/core/mappers/modules.mapper';
import { HomeFacade } from '@home/home.facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { LoginType } from '@app/entities/login-type';
import { endCallWebView } from '@app/modules/core/util/redirect-login';
import { countryModulesByWidgetsConfig } from '@home/mappers/home.mapper';
import { userAlertsRoute } from '@user-alerts/constants/user-alerts-routes.constants';
import { ChipActionStatus } from '@design-system/components/molecules/chip-action/entities/chip-action.types';
import {
  HeaderType, HeaderWidget, HeaderWidgetConfig, HeaderWidgetType
} from '@design-system/components/templates/header/entities/header.types';
import { AnimatedHeaderState, DsHeaderConfig, HeaderPageType } from '@header/entities/ds-header-config';
import { NavigationService } from '@app/services/navigation.service';
import { Transitions } from '@ux/animations/animations';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { goToSupportHelper, goToWhatsappSupportHelper } from '@app/helpers/open-url.helper';
import { productHomeRoute } from '@app/constants/app-routing.constants';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';

@Component({
  selector: 'app-header',
  templateUrl: './header.container.html',
  styleUrls: ['./header.container.sass'],
  animations: Transitions,
  standalone: false
})
export class HeaderContainer implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('modulesCont') modulesCont: ElementRef;
  @ViewChildren('anchor') anchorList: QueryList<ElementRef>;

  public homeRootRoute: string = homeRootRoute;
  public showLoader: boolean = false;
  public showAlertsManagement: boolean = false;
  public isAlertListMode: boolean = false;
  public hideTabs = false;
  public animatedHeaderState = AnimatedHeaderState.Init;

  public readonly headerHomeLg = HeaderType.HomeLG;

  public readonly isActiveMobileInterface = isActiveMobileInterface();

  private subscriptions: Subscription[] = [];
  private subscriptionsHome: Subscription = new Subscription();
  private animatedHeaderOffset = 0;

  private readonly mobileScrollTopAnimation = 190;

  constructor(
    private readonly router: Router,
    private readonly transloco: TranslocoService,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly searchBarFacade: BaseSearchBarFacade,
    private readonly navigationService: NavigationService,
    private appFacade: AppFacade,
    private facade: HeaderFacade,
    private homeFacade: HomeFacade,
    private headerService: HeaderService,
    private mobileDeviceService: MobileDeviceService,
    private domManipulateService: DomManipulateService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  @HostListener('window:scroll', ['$event']) onWindowScroll(): void {
    this.isMobile$.subscribe(isMobile => {
      this.animateHeader(isMobile);
    }).unsubscribe();
  }

  ngOnInit() {
    this.updateHideTabs();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      setStatusBarColor();
      this.subscriptions.push(
        this.navigationService.events$.pipe(
          filter(({ event }) => event instanceof ActivationEnd || event instanceof NavigationEnd),
          tap(({ rootRoute }) => {
            this.showLoader = false;
            this.animatedHeaderState = AnimatedHeaderState.Init;
            this.facade.setDsHeaderConfig(rootRoute?.snapshot?.data?.dsHeaderConfig);
          }),
          switchMap(() => this.isMobile$),
          delay(10),
          tap((isMobile) => {
            this.animateHeader(isMobile);
          }),
        ).subscribe()
      );
      this.subscriptionsHome.add(
        this.appFacade.isLoggedIn$.pipe(
          distinctUntilChanged(),
          switchMap(isLoggedIn => zip(
            this.isMobile$.pipe(take(1)),
            iif(
              () => !!isLoggedIn,
              this.appFacade.user$.pipe(filter(user => !isEmpty(user) && !!user?.id)),
              of(null)
            )
          ).pipe(
            take(1),
            tap(([isMobile]) => {
              if (!isMobile && isLoggedIn) {
                this.homeFacade.fetchUserHistoryListByHome();
              }
            })
          ))
        ).subscribe()
      );
      this.subscriptionsHome.add(
        this.appFacade.history$.subscribe(history => {
          this.appFacade.setHistoryToSessionStorageAction(history);
        }));
      this.subscriptionsHome.add(
        this.route.queryParams.subscribe(params => {
          this.updateHideTabs();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptionsHome.unsubscribe();
  }

  get usePlaceholder$(): Observable<boolean> {
    return combineLatest([this.isMobile$, this.homeFacade.isLoadingHome$, this.dsHeaderConfig$]).pipe(
      map(([isMobile, isLoadingHome, dsHeaderConfig]) => {
        return !this.isBrowser || (!isMobile && dsHeaderConfig?.pageType === HeaderPageType.HomePage && isLoadingHome);
      }));
  }

  get isGenericFixed$(): Observable<boolean> {
    return combineLatest([this.dsHeaderConfig$, this.dsHeaderEnabledSearchTabs$]).pipe(
      map(([dsHeaderConfig, dsHeaderEnabledSearchTabs]) => {
        return dsHeaderConfig?.pageType === HeaderPageType.SearchPage ?
          dsHeaderConfig?.type === HeaderType.Generic && dsHeaderEnabledSearchTabs : dsHeaderConfig?.type === HeaderType.Generic
      }));
  }

  get isTransparent$(): Observable<boolean> {
    return this.dsHeaderConfig$.pipe(
      map((dsHeaderConfig) => dsHeaderConfig?.pageType === HeaderPageType.SearchPage));
  }

  get dsHeaderConfig$(): Observable<DsHeaderConfig> {
    return this.facade.dsHeaderConfig$;
  }

  get showHeader$(): Observable<boolean> {
    return combineLatest([this.isMobile$, this.homeFacade.isLoadingHome$, this.dsHeaderConfig$]).pipe(
      map(([isMobile, isLoadingHome, dsHeaderConfig]) =>
        isMobile && dsHeaderConfig?.pageType === HeaderPageType.HomePage ? !isLoadingHome : true)
    );
  }

  get dsHeaderEnabledSearchTabs$(): Observable<boolean> {
    return combineLatest([this.dsHeaderConfig$, this.showTabsMobile$, this.usePlaceholder$])
      .pipe(map(([dsHeaderConfig, showTabsMobile, usePlaceholder]) => {
        return !usePlaceholder && showTabsMobile && dsHeaderConfig?.pageType === HeaderPageType.SearchPage;
      }));
  }

  get widgets$(): Observable<HeaderWidgetConfig[]> {
    return combineLatest([
      this.isMobile$, this.showSupport$, this.headerWidgetTitle$, this.isLoggedIn$, this.dsHeaderConfig$, this.usePlaceholder$
    ]).pipe(
      map(([isMobile, showSupport, headerWidgetTitle, isLoggedIn, dsHeaderConfig, usePlaceholder]) => {
        return !usePlaceholder ? [
          {
            Type: HeaderWidgetType.Back,
            Loading: this.showLoader
          },
          {
            Type: HeaderWidgetType.Title,
            Label: headerWidgetTitle,
            LabelIcon: dsHeaderConfig?.titleIcon as Icon ?? null,
            LabelAlign: 'center'
          },
          isMobile && this.isHomeRoute && {
            Type: HeaderWidgetType.InputSearch,
            Label: this.getLabel('TITLE_SEARCH_HOME'),
          },
          isMobile && this.isHomeRoute && isLoggedIn && {
            Type: HeaderWidgetType.Alert,
            IsActivated: this.showAlertsManagement,
            Label: this.getLabel('HEADER_MODULE.TITLES.USER_ALERTS'),
          },
          (isMobile ? this.isHomeRoute && showSupport : showSupport) && {
            Type: HeaderWidgetType.Support,
            Label: this.getLabel('USER_HISTORY_MODULE.DETAIL.HELP_CENTER'),
          },
          !isMobile && isLoggedIn && {
            Type: HeaderWidgetType.Alert,
            IsActivated: this.showAlertsManagement,
            Label: this.getLabel('HEADER_MODULE.TITLES.USER_ALERTS'),
          },
          (isMobile ? (this.isHomeRoute || (this.isHomeSubRoute && isLoggedIn)) : true) && {
            Type: HeaderWidgetType.MyTrips,
            Label: this.getLabel('HEADER_MODULE.TITLES.USER_HISTORY'),
            IsActivated: this.myFlightsActive(dsHeaderConfig?.pageType)
          },
          {
            Type: HeaderWidgetType.Rappi,
            Label: this.getLabel('HEADER_MODULE.GO_TO'),
            Link: this.headerLink
          },
        ] : [];
      })
    );
  }

  get headerAnimation$(): Observable<number> {
    return this.dsHeaderConfig$.pipe(
      map(dsHeaderConfig => {
        return this.isBrowser && this.mobileDeviceService.isMobileAgent &&
          (
            dsHeaderConfig?.pageType === HeaderPageType.ResultsPage ||
            dsHeaderConfig?.pageType === HeaderPageType.ResultDetailPage ||
            dsHeaderConfig?.pageType === HeaderPageType.BookingPage
          ) ? this.navigationService.animationValue : null;
      })
    );
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get countryModulesCollection(): ModuleEntity[] {
    return COUNTRY_MODULES_COLLECTION;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }

  get user$(): Observable<UserInfo> {
    return this.appFacade.user$;
  }

  get currentRoute(): string {
    return this.headerService.currentRoute;
  }

  get isHomeRoute(): boolean {
    return this.currentRoute === this.homeRootRoute;
  }

  get isHomeSubRoute(): boolean {
    return this.currentRoute.includes(productHomeRoute) && !this.currentRoute.includes(homeItemDetailRootRoute);
  }

  get headerLink(): string {
    return ENV.header_redirect.link;
  }

  get showSupport$(): Observable<boolean> {
    return zip(
      this.isLoggedIn$,
      this.user$,
    ).pipe(
      map(([isLoggedIn, user]) => isLoggedIn &&
        (user?.has_rappicard || user?.loyalty?.type === UserLoyaltyType.Diamond || user?.is_prime))
    );
  }

  get showTabsMobile$(): Observable<boolean> {
    return combineLatest([
      this.isMobile$,
      of(this.hideTabs)
    ]).pipe(
      map(([isMobile, hideTabs]) => isMobile && !hideTabs)
    );
  }

  get hideTabsMobile$(): Observable<boolean> {
    return this.showTabsMobile$.pipe(
      map(value => !value)
    );
  }

  get labelByModules$(): Observable<string> {
    return this.hideTabsMobile$.pipe(
      switchMap(hideTabs => {
        if (!hideTabs) {
          return this.transloco.selectTranslate('TITLE_SEARCH_HOME');
        } else {
          return this.getLabelBasedOnRoute().pipe(
            switchMap((key) => this.transloco.selectTranslate(key))
          );
        }
      })
    );
  }

  get showCloseWebviewModal$(): Observable<boolean> {
    return this.appFacade.showCloseWebviewModal$;
  }

  get countryModulesCollection$(): Observable<ModuleEntity[]> {
    return this.homeFacade.homeData$.pipe(
      map(homeData => countryModulesByWidgetsConfig(homeData?.widgets) as ModuleEntity[])
    );
  }

  private get headerWidgetTitle$(): Observable<string> {
    return combineLatest([
      this.dsHeaderConfig$, this.labelByModules$, this.isMobile$
    ]).pipe(
      map(([dsHeaderConfig, labelByModules, isMobile]) => {
        if (isMobile) {
          if (dsHeaderConfig?.pageType === HeaderPageType.SearchPage) {
            return labelByModules;
          } else {
            return dsHeaderConfig?.titleKey ? this.getLabel(`HEADER_MODULE.TITLES.${dsHeaderConfig?.titleKey}`) : '';
          }
        } else {
          return '';
        }
      })
    );
  }

  public chipActionEvent(module: ModuleEntity): void {
    this.router.navigate(module.route);
  }

  public getChipStatus(isActive: boolean): ChipActionStatus {
    return isActive ? ChipActionStatus.Selected : ChipActionStatus.Default;
  }

  private getLabelBasedOnRoute(): Observable<string> {
    const routeToLabelMap = {
      flights: 'HEADER_MODULE.HEADER_TABS.FLIGHTS',
      buses: 'HEADER_MODULE.HEADER_TABS.BUSES',
      hotels: 'HEADER_MODULE.HEADER_TABS.HOTELS',
      autos: 'HEADER_MODULE.HEADER_TABS.AUTOS',
      packages: 'HEADER_MODULE.HEADER_TABS.PACKAGES',
    };

    for (const route in routeToLabelMap) {
      if (this.currentRoute.includes(route)) {
        return of(routeToLabelMap[route]);
      }
    }
    return of(routeToLabelMap.flights);
  }

  public getCurrentUrl(): string {
    let result;
    if (typeof window !== 'undefined') {
      result = window.location.search;
    }
    return result;
  }

  public updateHideTabs(): void {
    const search = this.getCurrentUrl();
    const url = new URLSearchParams(search);
    const hideTabsParam = url.get('hide-tabs');
    this.hideTabs = hideTabsParam?.toLowerCase() === 'true';
  }

  public showTitle(style: Header): boolean {
    return style.showTitle && !isNullOrUndefined(style.title);
  }

  public myFlightsActive(pageType: HeaderPageType): boolean {
    return pageType === HeaderPageType.MyTripsPage;
  }

  public goBack(): void {
    this.showLoader = true;
    this.navigationService.navigateToBack();
  }

  public itemTrackBy(index: number, item: ModuleEntity): string {
    return item.name;
  }

  public moduleRouterLink(activate: boolean, route: string[]): string[] {
    return activate ? route : [];
  }

  public isActivateRoute(routeTitleKey: string, moduleName: string): boolean {
    return routeTitleKey === moduleName;
  }

  public redirectHome(): void {
    this.appFacade.historyReset();
    this.router.navigate([homeRootRoute]);
  }

  public onMyFlights(): void {
    combineLatest([this.isLoggedIn$, this.user$]).subscribe(
      ([isLoggedIn, user]) => {
        if (isLoggedIn && user) {
          this.router.navigate(['user-history', user?.id]);
        } else {
          this.headerService.forceUserLogin(LoginType.MyFlights);
        }
      }
    ).unsubscribe();
  }

  public toggleDesktopAlertsManagement(): void {
    this.showAlertsManagement = !this.showAlertsManagement; this.isAlertListMode = !this.isAlertListMode;
    this.domManipulateService.setBodyScroll(!this.showAlertsManagement);
  }

  public closeWebview(): void {
    endCallWebView();
  }

  public widgetAction(widget: HeaderWidget, isMobile: boolean): void {
    switch (widget?.Type) {
      case HeaderWidgetType.MyTrips:
        this.onMyFlights();
        break;
      case HeaderWidgetType.Alert:
        if (isMobile) {
          this.router.navigateByUrl(userAlertsRoute);
        } else {
          this.toggleDesktopAlertsManagement();
        }
        break;
      case HeaderWidgetType.Support:
        this.dsHeaderConfig$.pipe(
          take(1),
          tap((dsHeaderConfig) => {
            if (dsHeaderConfig?.type === HeaderType.Reservation) {
              goToSupportHelper();
            } else {
              goToWhatsappSupportHelper();
            }
          })
        ).subscribe().unsubscribe();
        break;
      case HeaderWidgetType.Back:
        this.goBack();
        break;
      case HeaderWidgetType.InputSearch:
        this.goToSearchModule();
        break;
      case HeaderWidgetType.Edit:
        zip(
          this.dsHeaderConfig$, this.searchBarFacade.editSearch$
        ).pipe(
          take(1),
          tap(([dsHeaderConfig, editSearch]) => {
            this.searchBarFacade.setEditSearchByProduct(editSearch, dsHeaderConfig?.productType);
          })
        ).subscribe().unsubscribe();
        break;
      default:
        this.redirectHome();
        break;
    }
  }

  public isValidDsHeaderConfig(headerType: HeaderType, pageType: HeaderPageType): boolean {
    return !isEmpty(headerType) && !isEmpty(pageType);
  }

  public isResultsHeader(headerType: HeaderType, pageType: HeaderPageType): boolean {
    return headerType === HeaderType.Results && pageType === HeaderPageType.ResultsPage;
  }

  public isReservationHeader(headerType: HeaderType, pageType: HeaderPageType): boolean {
    return headerType === HeaderType.Reservation && (
      pageType === HeaderPageType.TripDetailPage || pageType === HeaderPageType.LastTripDetailPage ||
      pageType === HeaderPageType.TripDetailCancellationPage
    );
  }

  private getLabel(key: string): string {
    const translate = this.transloco.translate(key);
    return translate === key ? '' : translate;
  }

  private goToSearchModule(): void {
    this.homeFacade.homeData$.pipe(
      tap((homeData) => {
        const { widgets } = homeData;
        this.router.navigate([...(MODULES_ROUTING?.[widgets?.split(',')[0]])]);
      })
    ).subscribe().unsubscribe();
  }

  private animateHeader(isMobile: boolean): void {
    if (isMobile) {
      this.animatedHeaderOffset = window?.pageYOffset || document?.documentElement?.scrollTop || document?.body?.scrollTop || 0;
      this.animatedHeaderState = this.animatedHeaderOffset >= this.mobileScrollTopAnimation ?
        AnimatedHeaderState.Open : AnimatedHeaderState.Closed;
      this.cdr.detectChanges();
    }
  }
}
